/**
 * Labstep
 *
 * @module components/EntityUserRole/SelectPanel
 * @desc EntityUserRole SelectPanel
 */

import { EntityActionDelete } from 'labstep-web/components/Entity/Action/Delete';
import { EntityUserRoleEditModal } from 'labstep-web/components/EntityUserRole/EditModal';
import { EntityCreateContainer } from 'labstep-web/containers/Entity/Create';
import { ActionComponent } from 'labstep-web/core/Action/Component';
import { Dropdown } from 'labstep-web/core/Dropdown';
import { DropdownPortal } from 'labstep-web/core/Dropdown/Portal';
import { SectionHeader } from 'labstep-web/core/Header';
import { Icon } from 'labstep-web/core/Icon';
import { Select } from 'labstep-web/core/Select';
import { useToggle } from 'labstep-web/hooks/toggle';
import { EntityUserRole } from 'labstep-web/models/entity-user-role.model';
import { getHumanReadableEntityName } from 'labstep-web/services/i18n.service';
import type React from 'react';
import { useState } from 'react';
import type { FormatOptionLabelMeta } from 'react-select';
import styles from './styles.module.scss';
import type { EntityUserRoleSelectPanelProps, Option } from './types';

export const EntityUserRoleSelectPanel: React.FC<
  EntityUserRoleSelectPanelProps
> = ({
  entityType,
  systemOptions,
  group,
  onChange,
  onBlur,
  allowDelete,
  defaultSelectedRoleId,
  openMenuOnFocus,
}) => {
  const [selectedEntityUserRoleId, setSelectedEntityUserRoleId] =
    useState<EntityUserRole['id'] | null>(
      defaultSelectedRoleId ?? null,
    );

  const selectedEntityUserRole = selectedEntityUserRoleId
    ? group.entity_user_roles.find(
        (e) => e.id === selectedEntityUserRoleId,
      )
    : null;

  const [searchText, setSearchText] = useState<string>('');

  const newRoleOption: Option = {
    label: searchText === '' ? 'New Role' : `New Role: ${searchText}`,
    value: 'custom',
    description: '',
    icon: 'add',
  };

  const options: Option[] = [
    ...group.entity_user_roles.map(
      (role) =>
        ({
          label: role.name,
          value: role.id,
          description: role.description,
          icon: 'user circle',
          entity: role,
        }) as Option,
    ),
    newRoleOption,
  ];

  const [isToggled, toggle] = useToggle();

  const handleInputChange = (searchInput: string) => {
    setSearchText(searchInput);
  };

  return (
    <EntityCreateContainer
      entityName={EntityUserRole.entityName}
      parentId={group.id}
      parentName={group.entityName}
    >
      {({ create, status }) => (
        <>
          <Select
            isCreateable
            autoFocus={!selectedEntityUserRoleId}
            openMenuOnFocus={openMenuOnFocus}
            onBlur={!isToggled && onBlur}
            isDisabled={(status && status.isFetching) || false}
            onChange={(option: Option) => {
              if (option.value === 'custom') {
                create(
                  { name: searchText || null },
                  {
                    onSuccess: ({ response }) => {
                      const id = response.result;
                      setSelectedEntityUserRoleId(id);
                    },
                  },
                );
                toggle();
              }
              onChange(option.entity);
              setSelectedEntityUserRoleId(
                option.value as EntityUserRole['id'],
              );
            }}
            onInputChange={handleInputChange}
            options={options}
            components={{
              SectionHeader,
              DropdownIndicator: () => null,
              IndicatorSeparator: () => null,
            }}
            formatOptionLabel={(
              option: Option,
              data: FormatOptionLabelMeta<Option>,
            ) => {
              return (
                <div className={styles.groupOption}>
                  {option.icon && <Icon name={option.icon} />}
                  <div>
                    <p className={styles.optionLabel}>
                      {option.label}
                    </p>
                    <p className={styles.optionDescription}>
                      {option.description}
                    </p>
                  </div>
                  <div className={styles.configureButton}>
                    {!systemOptions[0].options.includes(option) &&
                      option.value !== 'custom' && (
                        <DropdownPortal
                          data-testid="entity-user-role-configure-dropdown"
                          pointing="top right"
                          trigger={
                            data.context === 'menu' && (
                              <ActionComponent
                                dataCy="components/EntityUserRole/SelectPanel:trigger"
                                type="icon"
                                icon={'ellipsis vertical'}
                              />
                            )
                          }
                          xOffset={25}
                          yOffset={-10}
                        >
                          <Dropdown.Item
                            data-cy="components/EntityUserRole/SelectPanel:configure"
                            onClick={() => {
                              setSelectedEntityUserRoleId(
                                option.entity
                                  ?.id as EntityUserRole['id'],
                              );
                              toggle();
                            }}
                          >
                            <Icon name="cog" />
                            Configure
                          </Dropdown.Item>
                          {allowDelete && option.entity && (
                            <EntityActionDelete
                              entity={option.entity}
                            />
                          )}
                        </DropdownPortal>
                      )}
                  </div>
                </div>
              );
            }}
            value={
              selectedEntityUserRole
                ? options.filter(
                    (option) =>
                      option.entity === selectedEntityUserRole,
                  )
                : `Search or select a ${getHumanReadableEntityName(
                    entityType,
                  ).toLowerCase()}...`
            }
            formatSelectedOption={(option: Option) => (
              <div className={styles.groupOption}>
                <div>
                  <p className={styles.optionLabel}>{option.label}</p>
                  <p className={styles.optionDescription}>
                    {option.description}
                  </p>
                </div>
              </div>
            )}
          />
          {isToggled && selectedEntityUserRole && (
            <EntityUserRoleEditModal
              entityUserRole={selectedEntityUserRole}
              onClose={() => {
                onChange(selectedEntityUserRole);
                setSelectedEntityUserRoleId(null);
              }}
            />
          )}
        </>
      )}
    </EntityCreateContainer>
  );
};
